/**
 * Hierarchical Tag System for CRM
 * Supports parent-child relationships with predefined ISP tag hierarchy
 */

import type { CrmTag, CrmHierarchicalTag } from './types'

export interface CrmTagTreeNode {
  id: string
  name: string
  parentId: string | null
  emoji?: string
  description?: string
  children?: CrmTagTreeNode[]
  color?: string
  isActive: boolean
}

export interface CrmTagPath {
  tagId: string
  path: string[] // Array of tag names from root to this tag
  fullPath: string // "parent > child > grandchild"
}

/**
 * Predefined ISP tag hierarchy
 * 6 main categories with subcategories
 */
export const DEFAULT_ISP_TAG_HIERARCHY: Record<string, { emoji: string; children: string[] }> = {
  actions: {
    emoji: '⚡',
    children: ['connection', 'disconnection', 'reconnection', 'blocking', 'unblocking', 'other'],
  },
  consultations: {
    emoji: '💬',
    children: ['technical', 'tariffs', 'services', 'payments', 'other'],
  },
  suggestions: {
    emoji: '💡',
    children: ['new-service', 'tariff-change', 'other'],
  },
  'complaints-verbal': {
    emoji: '📢',
    children: ['quality', 'speed', 'other'],
  },
  'complaints-written': {
    emoji: '📄',
    children: ['official', 'social-media'],
  },
  retention: {
    emoji: '🎯',
    children: ['churn-prevention', 'win-back'],
  },
}

/**
 * Convert flat tag hierarchy to tree structure
 */
export function createTagTreeFromHierarchy(): CrmTagTreeNode[] {
  const nodes: CrmTagTreeNode[] = []

  Object.entries(DEFAULT_ISP_TAG_HIERARCHY).forEach(([parentKey, config]) => {
    const parentId = `tag-${parentKey}`

    // Create parent node
    const parentNode: CrmTagTreeNode = {
      id: parentId,
      name: parentKey,
      parentId: null,
      emoji: config.emoji,
      isActive: true,
      children: [],
    }

    // Create child nodes
    config.children.forEach((childKey) => {
      const childNode: CrmTagTreeNode = {
        id: `tag-${parentKey}-${childKey}`,
        name: childKey,
        parentId: parentId,
        isActive: true,
      }
      parentNode.children!.push(childNode)
    })

    nodes.push(parentNode)
  })

  return nodes
}

/**
 * Find a tag by ID in the tree
 */
export function findTagById(tree: CrmTagTreeNode[], tagId: string): CrmTagTreeNode | null {
  for (const node of tree) {
    if (node.id === tagId) {
      return node
    }
    if (node.children) {
      const found = findTagById(node.children, tagId)
      if (found) return found
    }
  }
  return null
}

/**
 * Get the full path of a tag (from root to this tag)
 */
export function getTagPath(tree: CrmTagTreeNode[], tagId: string): CrmTagPath | null {
  const buildPath = (
    nodes: CrmTagTreeNode[],
    targetId: string,
    currentPath: string[] = []
  ): string[] | null => {
    for (const node of nodes) {
      const newPath = [...currentPath, node.name]

      if (node.id === targetId) {
        return newPath
      }

      if (node.children) {
        const found = buildPath(node.children, targetId, newPath)
        if (found) return found
      }
    }
    return null
  }

  const path = buildPath(tree, tagId)
  if (!path) return null

  return {
    tagId,
    path,
    fullPath: path.join(' > '),
  }
}

/**
 * Get all child tags of a parent tag
 */
export function getChildTags(tree: CrmTagTreeNode[], parentId: string): CrmTagTreeNode[] {
  const parent = findTagById(tree, parentId)
  return parent?.children || []
}

/**
 * Get all root tags (tags without parent)
 */
export function getRootTags(tree: CrmTagTreeNode[]): CrmTagTreeNode[] {
  return tree.filter((node) => node.parentId === null)
}

/**
 * Flatten tree structure to array
 */
export function flattenTagTree(tree: CrmTagTreeNode[]): CrmTagTreeNode[] {
  const result: CrmTagTreeNode[] = []

  const flatten = (nodes: CrmTagTreeNode[]) => {
    nodes.forEach((node) => {
      result.push(node)
      if (node.children) {
        flatten(node.children)
      }
    })
  }

  flatten(tree)
  return result
}

/**
 * Search tags by name (case-insensitive)
 */
export function searchTags(tree: CrmTagTreeNode[], query: string): CrmTagTreeNode[] {
  const lowerQuery = query.toLowerCase()
  return flattenTagTree(tree).filter((tag) => tag.name.toLowerCase().includes(lowerQuery))
}

/**
 * Get tag by path string (e.g., "actions > connection")
 */
export function getTagByPath(tree: CrmTagTreeNode[], pathString: string): CrmTagTreeNode | null {
  const pathParts = pathString.split('>').map((p) => p.trim())

  const findByPath = (nodes: CrmTagTreeNode[], parts: string[]): CrmTagTreeNode | null => {
    if (parts.length === 0) return null

    const [current, ...remaining] = parts
    const node = nodes.find((n) => n.name === current)

    if (!node) return null
    if (remaining.length === 0) return node
    if (!node.children) return null

    return findByPath(node.children, remaining)
  }

  return findByPath(tree, pathParts)
}

/**
 * Check if a tag has children
 */
export function hasChildren(tag: CrmTagTreeNode): boolean {
  return !!tag.children && tag.children.length > 0
}

/**
 * Get all parent tags up to root
 */
export function getParentChain(tree: CrmTagTreeNode[], tagId: string): CrmTagTreeNode[] {
  const path = getTagPath(tree, tagId)
  if (!path) return []

  const chain: CrmTagTreeNode[] = []
  path.path.forEach((name) => {
    const tag = flattenTagTree(tree).find((t) => t.name === name)
    if (tag) chain.push(tag)
  })

  return chain
}

/**
 * Format tag name for display (replace hyphens with spaces, capitalize)
 */
export function formatTagName(name: string): string {
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Build hierarchical tag tree from flat array
 * Converts flat CrmTag[] to CrmHierarchicalTag[] with computed fields
 */
export function buildTagHierarchy(tags: CrmTag[]): CrmHierarchicalTag[] {
  // Create a map for quick lookup
  const tagMap = new Map<string, CrmHierarchicalTag>()
  const rootTags: CrmHierarchicalTag[] = []

  // Initialize all tags with computed fields
  tags.forEach((tag) => {
    const hierarchicalTag: CrmHierarchicalTag = {
      ...tag,
      children: [],
      level: 0,
      childrenIds: [],
      path: [],
      fullPath: '',
    }
    tagMap.set(tag.id, hierarchicalTag)
  })

  // Build hierarchy and compute fields
  tags.forEach((tag) => {
    const hierarchicalTag = tagMap.get(tag.id)!

    if (!tag.parentId) {
      // Root tag
      hierarchicalTag.level = 0
      hierarchicalTag.path = [tag.name]
      hierarchicalTag.fullPath = formatTagName(tag.name)
      rootTags.push(hierarchicalTag)
    } else {
      // Child tag
      const parent = tagMap.get(tag.parentId)
      if (parent) {
        parent.children!.push(hierarchicalTag)
        parent.childrenIds.push(tag.id)
        hierarchicalTag.level = parent.level + 1
        hierarchicalTag.path = [...parent.path, tag.name]
        hierarchicalTag.fullPath = parent.fullPath + ' > ' + formatTagName(tag.name)
      }
    }
  })

  return rootTags
}

/**
 * Check if setting a parent would create a cycle
 * Returns true if the operation is safe, false if it would create a cycle
 */
export function canSetParent(
  tags: CrmTag[],
  childId: string,
  newParentId: string | null
): boolean {
  // Setting to null (making it root) is always safe
  if (!newParentId) {
    return true
  }

  // Can't set self as parent
  if (childId === newParentId) {
    return false
  }

  // Build parent lookup map
  const parentMap = new Map<string, string | null>()
  tags.forEach((tag) => {
    parentMap.set(tag.id, tag.parentId ?? null)
  })

  // Temporarily set the new parent
  parentMap.set(childId, newParentId)

  // Check for cycles by walking up from newParentId
  const visited = new Set<string>()
  let currentId: string | null = newParentId

  while (currentId) {
    if (visited.has(currentId)) {
      // Cycle detected
      return false
    }
    if (currentId === childId) {
      // Would create a cycle
      return false
    }
    visited.add(currentId)
    currentId = parentMap.get(currentId) ?? null
  }

  return true
}

/**
 * Get all descendant tags (children, grandchildren, etc.)
 * Used for cascading operations like delete
 */
export function getAllDescendants(tags: CrmTag[], parentId: string): CrmTag[] {
  const descendants: CrmTag[] = []
  const childrenMap = new Map<string, CrmTag[]>()

  // Build children map
  tags.forEach((tag) => {
    if (tag.parentId) {
      const siblings = childrenMap.get(tag.parentId) || []
      siblings.push(tag)
      childrenMap.set(tag.parentId, siblings)
    }
  })

  // Recursively collect descendants
  const collectDescendants = (id: string) => {
    const children = childrenMap.get(id) || []
    children.forEach((child) => {
      descendants.push(child)
      collectDescendants(child.id)
    })
  }

  collectDescendants(parentId)
  return descendants
}

/**
 * Generate default ISP tag seed data
 * Creates the 6 main categories with subcategories
 */
export function generateDefaultTagSeedData(): CrmTag[] {
  const tags: CrmTag[] = []
  const timestamp = new Date().toISOString()

  const categories: Record<
    string,
    { emoji: string; color: string; children: Record<string, string> }
  > = {
    actions: {
      emoji: '⚡',
      color: '#3b82f6',
      children: {
        connection: 'Подключение нового клиента',
        disconnection: 'Отключение услуг',
        reconnection: 'Восстановление после отключения',
        blocking: 'Блокировка услуг',
        unblocking: 'Разблокировка услуг',
        'tariff-change': 'Смена тарифного плана',
      },
    },
    consultations: {
      emoji: '💬',
      color: '#10b981',
      children: {
        technical: 'Технические вопросы',
        tariffs: 'Консультация по тарифам',
        services: 'Дополнительные услуги',
        payments: 'Вопросы по оплате',
        coverage: 'Зона покрытия',
      },
    },
    suggestions: {
      emoji: '💡',
      color: '#f59e0b',
      children: {
        'new-service': 'Предложение новой услуги',
        'tariff-change': 'Изменение тарифа',
        improvement: 'Улучшение качества',
      },
    },
    'complaints-verbal': {
      emoji: '📢',
      color: '#ef4444',
      children: {
        quality: 'Качество связи',
        speed: 'Скорость интернета',
        staff: 'Обслуживание персонала',
        billing: 'Вопросы биллинга',
      },
    },
    'complaints-written': {
      emoji: '📄',
      color: '#dc2626',
      children: {
        official: 'Официальная жалоба',
        'social-media': 'Жалоба в соцсетях',
        regulator: 'Обращение в регулятор',
      },
    },
    retention: {
      emoji: '🎯',
      color: '#8b5cf6',
      children: {
        'churn-prevention': 'Удержание клиента',
        'win-back': 'Возврат клиента',
        loyalty: 'Программа лояльности',
      },
    },
  }

  Object.entries(categories).forEach(([categoryKey, categoryData]) => {
    const categoryId = `tag-${categoryKey}`

    // Create parent category
    tags.push({
      id: categoryId,
      name: categoryKey,
      color: categoryData.color,
      emoji: categoryData.emoji,
      description: `ISP категория: ${formatTagName(categoryKey)}`,
      parentId: null,
      usageCount: 0,
      isActive: true,
      isDefault: true,
      createdAt: timestamp,
      updatedAt: timestamp,
    })

    // Create child tags
    Object.entries(categoryData.children).forEach(([childKey, childDescription]) => {
      tags.push({
        id: `tag-${categoryKey}-${childKey}`,
        name: childKey,
        color: categoryData.color,
        emoji: undefined,
        description: childDescription,
        parentId: categoryId,
        usageCount: 0,
        isActive: true,
        isDefault: true,
        createdAt: timestamp,
        updatedAt: timestamp,
      })
    })
  })

  return tags
}

/**
 * Flatten hierarchical tree to array
 */
export function flattenHierarchicalTags(tree: CrmHierarchicalTag[]): CrmHierarchicalTag[] {
  const result: CrmHierarchicalTag[] = []

  const flatten = (nodes: CrmHierarchicalTag[]) => {
    nodes.forEach((node) => {
      result.push(node)
      if (node.children && node.children.length > 0) {
        flatten(node.children)
      }
    })
  }

  flatten(tree)
  return result
}

/**
 * Expand tag selection to include all descendants
 * Used for filtering - when parent tag selected, include all children
 *
 * Example:
 * - User selects "Действия" (actions)
 * - Automatically includes all subcategories: connection, disconnection, etc.
 */
export function expandWithChildren(
  tagIds: string[],
  allTags: CrmTag[]
): string[] {
  const result = new Set(tagIds)

  tagIds.forEach(tagId => {
    const descendants = getAllDescendants(allTags, tagId)
    descendants.forEach(desc => result.add(desc.id))
  })

  return Array.from(result)
}
